import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import SubmitButton from '../shared/submit-button'
import TextLink from '@/components/Form/TextLink'
import HelpText from '@/components/Form/HelpText'
import Iconify from '@/components/Iconify'

import useAuth from '../../hooks/use-auth'
import { useAuthNavigation } from '../../hooks/use-auth-navigation'
import { AuthModalBody } from '../auth-modal/auth-modal-body'
import { loginSchema } from '../../validations/auth-schemas'
import { AUTH_STEPS } from '../../constants/auth-steps'
import { WHATSAPP_SUPPORT_URL } from '../../constants/config'
import {
  getRememberLoginPreference,
  getRememberedUsername,
  tryLoadBrowserCredentials,
} from '../../utils/remember-login'
import {
  AuthCheckboxField,
  AuthTextField,
  FacebookLoginButton,
  Logo,
  Title,
  TurnstileField,
} from '../shared'

export default function LoginForm () {
  const usernameRef = useRef()
  const passwordRef = useRef()
  const autofillFetchedRef = useRef(false)
  const {
    submitting,
    sendCredentials,
    isFailedLogin,
    responseFacebook,
    turnstileToken,
  } = useAuth()
  const { goToSignup, goToForgotPassword } = useAuthNavigation()

  const {
    control,
    handleSubmit,
    setValue,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
      rememberMe: false,
    },
    mode: 'onSubmit',
  })

  useEffect(() => {
    usernameRef.current?.focus()
  }, [])

  useEffect(() => {
    if (autofillFetchedRef.current) return
    autofillFetchedRef.current = true

    if (!getRememberLoginPreference()) return

    setValue('rememberMe', true)

    const savedUsername = getRememberedUsername()
    if (savedUsername) {
      setValue('username', savedUsername)
    }

    tryLoadBrowserCredentials().then((credentials) => {
      if (!credentials) return
      setValue('username', credentials.username)
      setValue('password', credentials.password)
    })
  }, [setValue])

  const onSubmit = ({ username, password, rememberMe }) => {
    sendCredentials(username, password, undefined, undefined, Boolean(rememberMe))
  }

  const submitFromPasswordField = () => {
    handleSubmit(onSubmit)()
  }

  return (
    <AuthModalBody enableAutofillStyles>
      <Logo src='/images/Pickwin.png' alt='Pickwin logo' />
      <Title>Iniciar Sesión</Title>

      <FacebookLoginButton
        callback={responseFacebook}
        loading={submitting === 'facebook-loading'}
      />

      <AuthTextField
        control={control}
        name='username'
        ref={usernameRef}
        iconSrc='/images/Icono Mail.png'
        placeholder='Email o nombre de usuario'
        autoComplete='username'
        onEnter={() => passwordRef.current?.focus()}
      />

      <AuthCheckboxField
        control={control}
        name='rememberMe'
        label='Recordar datos'
        style={{ marginLeft: '0.7em' }}
      />

      <AuthTextField
        control={control}
        name='password'
        type='password'
        ref={passwordRef}
        placeholder='Contraseña'
        iconSrc='/images/Icono candado.png'
        autoComplete='current-password'
        onEnter={submitFromPasswordField}
      />

      <TextLink onClick={goToForgotPassword} style={{ marginBottom: '0.5em' }}>
        ¿Has olvidado tu contraseña?
      </TextLink>

      {isFailedLogin && (
        <HelpText color='red'>
          <Iconify
            icon='dashicons:warning'
            style={{
              fontSize: '1.4em',
              verticalAlign: 'bottom',
              marginRight: '0.2em',
            }}
          />
          <span>Por favor revisa tus datos, e inténtalo nuevamente.</span>
        </HelpText>
      )}

      <TurnstileField step={AUTH_STEPS.LOGIN_FORM} />

      <SubmitButton
        disabled={!turnstileToken}
        onClick={handleSubmit(onSubmit)}
        loading={submitting === 'email-loading'}
      >
        Iniciar sesión
      </SubmitButton>

      <HelpText>
        ¿No tienes una cuenta? <strong onClick={goToSignup}>Crear cuenta</strong>
      </HelpText>
      <HelpText style={{ marginBottom: 0 }}>
        Si tienes algún problema con tu registro y requieres ayuda, contáctanos
      </HelpText>

      <WhatsApp href={WHATSAPP_SUPPORT_URL} target='_blank' rel='noreferrer'>
        <img src='/images/Icono whats.png' alt='Whatsapp logo button' />
      </WhatsApp>
    </AuthModalBody>
  )
}

const WhatsApp = styled.a`
  margin: 0 auto;
  cursor: pointer;

  img {
    max-width: 35px;
  }
`
